import type { FC, ReactNode } from 'react';

import classNames from 'classnames';
import qs from 'query-string';
import { connect } from 'react-redux';
import { useHistory } from 'react-router';
import { compose } from 'redux';

import CapsuleIcon from 'src/app/components/Elements/CapsuleIcon';
import useAuth from 'src/app/hooks/useAuth';
import { medications, redirectHome } from 'src/app/router/paths';
import { setPreviousOnboardingStep } from 'src/app/state/actions';
import { getOnboardingCurrentIndex, getOnboardingStepsLength } from 'src/app/state/selectors';
import type { Dispatch, RootState } from 'src/app/state/store';
import { CAPSULE_ICON_STRINGS } from 'src/consts';

import { MobilePreviousButton, StyledStepCount } from '../styles';
import styles from '../styles.module.scss';

const mapStateToProps = (state: RootState) => {
  return {
    currentStepIndex: getOnboardingCurrentIndex(state),
    totalSteps: getOnboardingStepsLength(state),
  };
};
const mapDispatchToProps = (dispatch: Dispatch) => ({
  redirectHome,
  onSetPreviousOnboardingStep: () => dispatch(setPreviousOnboardingStep()),
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ReturnType<typeof mapStateToProps>;

export interface StepCountProps extends Partial<PropsFromRedux> {
  hideCount?: boolean;
  showPreviousButton: boolean;
  showCountDesktop?: boolean;
  handlePrev?: () => void;
  customStepCount?: ReactNode;
  customHandlePrevText?: string;
}

export const StepCountRenderer: FC<StepCountProps> = ({
  currentStepIndex,
  totalSteps,
  redirectHome,
  onSetPreviousOnboardingStep,
  hideCount,
  showPreviousButton = true,
  handlePrev,
  customStepCount,
  showCountDesktop,
  customHandlePrevText = 'Back',
}) => {
  const { isLoggedIn } = useAuth();
  const history = useHistory();

  const handlePreviousClick = (currentStepIndex: number) => () => {
    if (handlePrev) {
      handlePrev();
    } else if (currentStepIndex === 0) {
      // TODO: this logic should be on the step itself.
      const referral = qs.parse(window.location.search)?.cp_referral as string | undefined;
      if (isLoggedIn) {
        history.push(medications.route);
      } else if (referral) {
        history.push(`/l/${referral}`);
      } else {
        redirectHome();
      }
    } else {
      onSetPreviousOnboardingStep();
    }
  };

  const StepCountComponent = customStepCount ?? StyledStepCount;

  return (
    <StepCountComponent>
      {showPreviousButton && (
        <MobilePreviousButton onClick={handlePreviousClick(currentStepIndex)} showCountDesktop={showCountDesktop}>
          <CapsuleIcon
            block
            className={classNames(styles.backArrow, 'fill-teal')}
            iconString={CAPSULE_ICON_STRINGS.system_nav_back_arrow}
          />
          {customHandlePrevText}
        </MobilePreviousButton>
      )}
      {!hideCount && (
        <div>
          {currentStepIndex + 1} of {totalSteps}
        </div>
      )}
    </StepCountComponent>
  );
};

export const StepCount = compose(connector)(StepCountRenderer);
